import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, GripVertical, Mail, Phone, ExternalLink, Copy, QrCode, Sparkles, Compass, Info, Bell } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Checkbox } from '../../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';

import { surveysAPI } from '../api';
import api from '../../../lib/api';
import { serializeSurveyDefinition } from '../utils/serializeSurveyDefinition';
import EnabledLanguagesPicker from '../../../components/shared/EnabledLanguagesPicker';
import ImageUploadControl from '../components/ImageUploadControl';
import SurveyAppearancePreview from '../components/SurveyAppearancePreview';
import BranchingChoicesEditor, { defaultBranchingChoices } from '../components/BranchingChoicesEditor';
import ClosingPageEditor from '../components/ClosingPageEditor';
import QuestionFlowControl from '../components/QuestionFlowControl';
import OrgLocationPicker from '../components/OrgLocationPicker';
import SurveyFlowMap from '../components/SurveyFlowMap';
import SurveyBuilderQuickTour, { quickTourHasSeen } from '../components/SurveyBuilderQuickTour';
import { analyzeSurveyFlow } from '../utils/surveyFlow';
import detectOverallExperienceDuplicate from '../utils/detectOverallExperienceDuplicate';
import { useAuth } from '../../../lib/auth';

const newQuestion = (order) => ({
    id: `q_${Math.random().toString(36).slice(2, 10)}`,
    text: { en: '', fr: '', es: '' },
    response_type: 'rating',
    rating_scale: { min: 1, max: 5 },
    enabled: true,
    required: false,
    order,
});

const SurveyEditor = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';
    const [survey, setSurvey] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [saving, setSaving] = useState(false);
    // v3.5.8 — Default to true for Super Admin so they're never locked out
    // by a transient /_status fetch failure.
    const [canManage, setCanManage] = useState(isSuperAdmin);
    const [dismissedTx, setDismissedTx] = useState(false);
    // v3.5.2 — Local-only viewport selector for the live preview.
    const [previewDevice, setPreviewDevice] = useState('phone'); // 'phone' | 'tablet' | 'desktop'

    // P_SURVEY_CARD_STYLE_PREVIEW_1.0 — the organization's own card_style
    // (voucher_settings.card_style), so "Inherit" previews what the backend's
    // resolve_card_style() would actually produce. Defaults to the same
    // 'frosted' the backend falls back to when the org never set one.
    const [orgCardStyle, setOrgCardStyle] = useState('frosted');

    // P_SURVEYS_GUIDANCE_PARITY_1.0 — Quick Tour modal state. Auto-opens
    // the first time an author lands on the editor (per browser); the
    // header "Quick tour" button re-opens it on demand.
    const [tourOpen, setTourOpen] = useState(false);

    // P_SURVEY_EDITOR_UX_ENHANCEMENTS_1.0 — unsaved-changes guard
    // when the user clicks "Back to Surveys" while edits are pending.
    const [unsavedConfirmOpen, setUnsavedConfirmOpen] = useState(false);

    const PREVIEW_WIDTHS = { phone: 390, tablet: 768, desktop: null };
    // v3.5.3 — Window dimensions for "Open Public Link" — match the device toggle.
    const PREVIEW_WINDOW_SIZES = {
        phone:   { width: 390, height: 844 },
        tablet:  { width: 768, height: 1024 },
        desktop: null,
    };

    // P_SURVEY_EDITOR_UX_ENHANCEMENT_1.2 (2026-06-29) — guarantee the
    // editor opens scrolled to the top. Browsers preserve the previous
    // route's scroll position by default; when a user came from a
    // long survey list (or any scrolled page) the editor would mount
    // with the toolbar row already above the viewport — invisible
    // behind the fixed top command bar — which produced the
    // "missing Back to Surveys button" reports in prod.
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, [id]);

    // Read the org default once the survey (and so its org) is known.
    useEffect(() => {
        const orgId = survey?.organization_id;
        if (!orgId) return;
        let cancelled = false;
        api.get('/voucher-settings', { params: { organization_id: orgId } })
            .then(r => {
                const s = r.data?.card_style;
                if (!cancelled && ['frosted', 'clear', 'solid'].includes(s)) setOrgCardStyle(s);
            })
            .catch(() => { /* not readable for this role — keep the 'frosted' fallback */ });
        return () => { cancelled = true; };
    }, [survey?.organization_id]);

    const openPublicLink = () => {
        if (!survey?.slug) return;
        const url = `${window.location.origin}/s/${encodeURIComponent(survey.slug)}`;
        const dims = PREVIEW_WINDOW_SIZES[previewDevice];
        if (dims) {
            window.open(
                url,
                `survey-preview-${previewDevice}`,
                `width=${dims.width},height=${dims.height},noopener,noreferrer`,
            );
        } else {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    // v3.5.4 — Copy public link to clipboard with toast confirmation.
    const copyPublicLink = async () => {
        if (!survey?.slug) return;
        const url = `${window.location.origin}/s/${encodeURIComponent(survey.slug)}`;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                // Legacy-browser fallback (http or non-secure context).
                const ta = document.createElement('textarea');
                ta.value = url;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            toast.success(t('surveys.appearance.linkCopied', 'Link copied'));
        } catch {
            toast.error(t('surveys.appearance.linkCopyFailed', 'Unable to copy link'));
        }
    };

    // v3.5.5 — Download a 1024×1024 PNG QR code of the public survey URL.
    // Uses the offscreen <QRCodeCanvas> rendered at the bottom of this component
    // (ref'd via qrCanvasRef) and exports it to a blob for browser download.
    const qrCanvasRef = useRef(null);
    const getQrCanvas = () => {
        const container = qrCanvasRef.current;
        return container ? container.querySelector('canvas') : null;
    };
    const downloadQrCode = () => {
        if (!survey?.slug) return;
        const canvas = getQrCanvas();
        if (!canvas) {
            toast.error(t('surveys.appearance.qrFailed', 'Unable to generate QR code'));
            return;
        }
        try {
            const dataUrl = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `survey-qr-${survey.slug}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success(t('surveys.appearance.qrDownloaded', 'QR code downloaded'));
        } catch {
            toast.error(t('surveys.appearance.qrFailed', 'Unable to generate QR code'));
        }
    };

    // v3.5.6 — Branded QR: compose 1200×1400 PNG with optional logo, QR, caption, URL.
    // Reuses the same offscreen QRCodeCanvas as the plain download — nothing new fetched.
    // Pure client-side canvas composition; no backend, no new dependencies.
    const downloadBrandedQr = async () => {
        if (!survey?.slug) return;
        const qrCanvas = getQrCanvas();
        if (!qrCanvas) {
            toast.error(t('surveys.appearance.qrFailed', 'Unable to generate QR code'));
            return;
        }

        const W = 1200;
        const H = 1400;
        const out = document.createElement('canvas');
        out.width = W;
        out.height = H;
        const ctx = out.getContext('2d');
        // Background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, W, H);

        // Best-effort logo load (skipped silently on failure / cross-origin taint).
        let logoImg = null;
        if (survey.logo_url) {
            try {
                logoImg = await new Promise((resolve, reject) => {
                    const im = new Image();
                    im.crossOrigin = 'anonymous';
                    im.onload = () => resolve(im);
                    im.onerror = reject;
                    im.src = survey.logo_url;
                });
            } catch {
                logoImg = null;
            }
        }

        // Layout (vertical stack, centered horizontally).
        const LOGO_MAX_W = 200;
        const LOGO_BOX_H = logoImg ? 140 : 0;
        const QR_SIZE = 1024;
        const topPad = 40;
        let y = topPad;

        if (logoImg) {
            const ratio = Math.min(LOGO_MAX_W / logoImg.width, 120 / logoImg.height, 1);
            const lw = logoImg.width * ratio;
            const lh = logoImg.height * ratio;
            const lx = (W - lw) / 2;
            const ly = y + (LOGO_BOX_H - lh) / 2;
            ctx.drawImage(logoImg, lx, ly, lw, lh);
            y += LOGO_BOX_H + 20;
        }

        // Center QR horizontally
        const qrX = (W - QR_SIZE) / 2;
        ctx.drawImage(qrCanvas, qrX, y, QR_SIZE, QR_SIZE);
        y += QR_SIZE + 30;

        // Caption
        const caption = t('surveys.appearance.qrCaption', 'Scan to share feedback');
        ctx.fillStyle = '#111827';
        ctx.textAlign = 'center';
        ctx.font = '600 40px -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
        ctx.fillText(caption, W / 2, y + 40);
        y += 70;

        // URL
        const url = `${window.location.origin}/s/${survey.slug}`;
        ctx.fillStyle = '#475569';
        ctx.font = '400 26px ui-monospace, "SFMono-Regular", Menlo, Monaco, Consolas, monospace';
        ctx.fillText(url, W / 2, y + 30);

        // Export → retry without logo once if the logo tainted the canvas
        // (e.g., CORS-unfriendly backend for logo_url). QR + text are always safe.
        const tryExport = () => {
            try {
                return out.toDataURL('image/png');
            } catch {
                return null;
            }
        };

        let dataUrl = tryExport();
        if (!dataUrl && logoImg) {
            // Rebuild without the logo
            ctx.clearRect(0, 0, W, H);
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, W, H);
            let y2 = topPad;
            ctx.drawImage(qrCanvas, qrX, y2, QR_SIZE, QR_SIZE);
            y2 += QR_SIZE + 30;
            ctx.fillStyle = '#111827';
            ctx.textAlign = 'center';
            ctx.font = '600 40px -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
            ctx.fillText(caption, W / 2, y2 + 40);
            y2 += 70;
            ctx.fillStyle = '#475569';
            ctx.font = '400 26px ui-monospace, "SFMono-Regular", Menlo, Monaco, Consolas, monospace';
            ctx.fillText(url, W / 2, y2 + 30);
            dataUrl = tryExport();
        }

        if (!dataUrl) {
            toast.error(t('surveys.appearance.qrFailed', 'Unable to generate QR code'));
            return;
        }

        try {
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `survey-qr-branded-${survey.slug}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success(t('surveys.appearance.qrDownloaded', 'QR code downloaded'));
        } catch {
            toast.error(t('surveys.appearance.qrFailed', 'Unable to generate QR code'));
        }
    };

    const lang = i18n.language || 'en';
    // P_AUTHORING_PARITY_1.0 — single source of truth for the
    // authoring language used by the single-language inputs below.
    // Persisted on the survey doc so reopening the editor reuses the
    // last choice; falls back to UI language for legacy surveys.
    const authoringLang = (survey && (survey.authoring_language || lang)) || 'en';
    // Per-answer alerts need somewhere to text: the survey's existing phones.
    const hasAlertPhones = ((survey?.notification_phones) || []).length > 0;

    useEffect(() => {
        surveysAPI.status().then(d => setCanManage(!!d.can_manage)).catch(() => setCanManage(isSuperAdmin));
        surveysAPI.get(id)
            .then(s => {
                setSurvey(s);
                setSnapshot(serializeSurveyDefinition(s, 'survey'));
                // P_SURVEYS_GUIDANCE_PARITY_1.0 — auto-open the Quick
                // Tour on the author's first visit to any survey editor
                // (per-browser flag in localStorage). Subsequent visits
                // are silent; the header "Quick tour" button is always
                // available to re-open it on demand.
                if (!quickTourHasSeen()) {
                    // Defer a tick so the editor visually settles first.
                    setTimeout(() => setTourOpen(true), 400);
                }
            })
            .catch(err => {
                toast.error(err?.response?.data?.detail || t('surveys.loadFailed'));
                navigate('/surveys');
            });
    }, [id]);

    const isDirty = useMemo(() => {
        if (!snapshot || !survey) return false;
        return serializeSurveyDefinition(survey, 'state') !== snapshot;
    }, [survey, snapshot]);

    if (!survey) return <div className="p-6">{t('common.loading')}</div>;

    const update = (patch) => setSurvey({ ...survey, ...patch });
    const updateName = (l, v) => update({ name: { ...(survey.name || {}), [l]: v } });
    const updateAlerts = (patch) => update({ alert_rules: { ...(survey.alert_rules || {}), ...patch } });

    const updateQuestion = (idx, patch) => {
        const qs = [...(survey.questions || [])];
        qs[idx] = { ...qs[idx], ...patch };
        update({ questions: qs });
    };
    const updateQuestionText = (idx, l, v) => {
        const qs = [...(survey.questions || [])];
        qs[idx] = { ...qs[idx], text: { ...(qs[idx].text || {}), [l]: v } };
        update({ questions: qs });
    };
    const addQuestion = () => {
        const qs = [...(survey.questions || []), newQuestion((survey.questions?.length || 0) + 1)];
        update({ questions: qs });
    };

    // P_BRANCHING_AUTHORING_TREE_1.0 — "Create New Branch" wiring.
    // Invoked when the author picks "Create New Branch" inside a
    // branching choice's Next Step selector. We:
    //   1. Append a fresh question to `survey.questions`.
    //   2. Point the source choice's `next_step` at the new question
    //      (type='jump', target_question_id=<new id>).
    //   3. Return the new question id so the editor can scroll to it.
    //
    // No backend / runtime change is needed — the persisted shape is
    // identical to a manual "Jump to specific question" wiring. This
    // is purely an authoring shortcut.
    const createBranchFromChoice = (sourceQuestionIdx, sourceChoiceIdx) => {
        const qs = [...(survey.questions || [])];
        if (!qs[sourceQuestionIdx]) return null;
        const newQ = newQuestion(qs.length + 1);
        // Default new branch question to free_text so the author can
        // immediately type the follow-up they want; they can flip the
        // type afterwards.
        newQ.response_type = 'free_text';
        // P_BRANCHING_TREE_ENGINE_1.2 — branch follow-ups must NOT
        // linear-advance into a sibling branch's question. Default the
        // new question's `next_step` to `end` so each branch
        // terminates naturally. The author can change this to "Jump
        // to" a common-resume question once the survey is more built
        // out.
        newQ.next_step = { type: 'end' };
        // Append the new question to the array.
        qs.push(newQ);
        // Wire the source choice to jump to it.
        const sourceChoices = (qs[sourceQuestionIdx].choices || []).map((c, i) => {
            if (i !== sourceChoiceIdx) return c;
            return {
                ...c,
                next_step: { type: 'jump', target_question_id: newQ.id },
            };
        });
        qs[sourceQuestionIdx] = { ...qs[sourceQuestionIdx], choices: sourceChoices };
        update({ questions: qs });
        // Defer scroll + focus until React applies the new question card.
        setTimeout(() => {
            const el = document.querySelector(`[data-question-id="${newQ.id}"]`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Briefly flash the new card so the author can see
                // exactly which question was created.
                el.classList.add('ring-2', 'ring-fuchsia-400');
                setTimeout(() => el.classList.remove('ring-2', 'ring-fuchsia-400'), 1600);
                // P_BRANCHING_AUTHORING_TREE_1.1 — auto-focus the new
                // question's text input so the author can immediately
                // type the follow-up wording with no extra clicks.
                // The input is the first <input> inside the card body
                // — we target it via the data-testid prefix instead of
                // a generic selector to stay resilient to future card
                // markup changes.
                const input = el.querySelector('input[data-testid^="q-text-"]');
                if (input) {
                    input.focus({ preventScroll: true });
                    // Position the caret at the end so a saved-then-
                    // edited question doesn't lose existing text on
                    // re-focus; for a brand-new question the value is
                    // empty so this is a no-op.
                    try {
                        const len = input.value?.length ?? 0;
                        input.setSelectionRange(len, len);
                    } catch { /* setSelectionRange may not apply to certain input types */ }
                }
            }
        }, 120);
        toast.success(t('surveys.branching.createdNewBranch', 'New branch question created'));
        return newQ.id;
    };
    const removeQuestion = (idx) => {
        const qs = (survey.questions || []).filter((_, i) => i !== idx);
        update({ questions: qs });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                name: survey.name,
                description: survey.description,
                organization_id: survey.organization_id || null,
                location_id: survey.location_id || null,
                enabled: survey.enabled,
                questions: survey.questions || [],
                alert_rules: survey.alert_rules || {},
                priority_rules: survey.priority_rules || {},
                notification_emails: survey.notification_emails || [],
                notification_phones: survey.notification_phones || [],
                enabled_languages: survey.enabled_languages ?? null,
                logo_url: survey.logo_url ?? null,
                logo_size: survey.logo_size || 'medium',
                display_mode: survey.display_mode || 'auto',
                background_image_url: survey.background_image_url ?? null,
                card_style: survey.card_style ?? null,
                source_language: authoringLang,
                authoring_language: authoringLang,
                closing_page: survey.closing_page || null,
                // P_SURVEY_EDITOR_UX_ENHANCEMENTS_1.0 — per-survey
                // toggle for the platform-level Overall Experience
                // initial screen. Default true if undefined.
                overall_experience_enabled:
                    survey.overall_experience_enabled !== false,
            };
            const updated = await surveysAPI.update(id, payload);
            setSurvey(updated);
            setSnapshot(serializeSurveyDefinition(updated, 'survey'));
            toast.success(t('common.saved'));
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('common.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4 sm:p-6 space-y-4 max-w-4xl mx-auto" data-testid="survey-editor-page">
            {/*
              * P_SURVEY_EDITOR_UX_ENHANCEMENT_1.2 (2026-06-29) — sticky toolbar.
              *
              * Production bug report 2026-06-29: the Back-to-Surveys button
              * was rendering at viewport y ≈ -21 in some sessions
              * (verified via getBoundingClientRect on portal.yulontap.com).
              * The button was in the DOM, display:flex, opacity:1, but
              * tucked above the viewport behind the fixed top command bar
              * (which itself stays pinned at y=0..56 via position:fixed).
              *
              * Root cause: this row sat at the very top of <main>'s
              * padding region. Any inherited scroll position, transient
              * scroll-restoration on route change, or page-mount animation
              * could nudge the row above the visible area while the
              * fixed top bar masked the gap, making it look like the
              * button was simply missing.
              *
              * Fix: pin this toolbar row beneath the fixed top command
              * bar (top = --preview-offset + bar height). It now stays
              * visible regardless of scroll position. Backdrop blur +
              * page-coloured background keep it readable when content
              * scrolls beneath.
              */}
            <div
                className="sticky top-[calc(var(--preview-offset,0px)+4rem)] lg:top-[calc(var(--preview-offset,0px)+3.5rem)] z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-slate-200/60 dark:border-slate-700/60 flex flex-wrap items-center justify-between gap-x-3 gap-y-2"
                data-testid="survey-editor-toolbar"
            >
                <Button
                    variant="outline"
                    onClick={() => {
                        // P_SURVEY_EDITOR_UX_ENHANCEMENTS_1.0 — guard
                        // against accidental data loss when there are
                        // unsaved edits in any tab.
                        if (isDirty) {
                            setUnsavedConfirmOpen(true);
                        } else {
                            navigate('/surveys');
                        }
                    }}
                    data-testid="survey-back-btn"
                    aria-label={t('surveys.editor.backToSurveys', 'Back to Surveys')}
                    className="flex-shrink-0 border-slate-300 dark:border-slate-600"
                >
                    <ArrowLeft className="w-4 h-4 mr-1.5" />
                    {t('surveys.editor.backToSurveys', 'Back to Surveys')}
                </Button>
                <div className="flex items-center gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setTourOpen(true)}
                        data-testid="survey-quick-tour-open-btn"
                        title={t('surveys.quickTour.openTooltip', 'Open the quick tour of the Survey Builder')}
                    >
                        <Compass className="w-3.5 h-3.5 mr-1.5" />
                        {t('surveys.quickTour.openButton', 'Quick tour')}
                    </Button>
                    <span
                        data-testid="survey-dirty-indicator"
                        className={`text-xs font-medium ${isDirty ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}
                    >
                        {isDirty ? t('common.pendingChanges') : t('common.allSettingsSaved')}
                    </span>
                    <Button onClick={handleSave} disabled={saving || !isDirty || !canManage} data-testid="survey-save-btn">
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? t('common.saving') : t('common.save')}
                    </Button>
                </div>
            </div>

            {!canManage && (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-900 dark:text-amber-200" data-testid="survey-editor-readonly-banner">
                    {t('surveys.readOnlyBanner')}
                </div>
            )}

            {survey?.translation_meta?.status === 'failed' && !dismissedTx && (
                <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-900 dark:text-amber-200 flex items-start justify-between gap-3" data-testid="translation-failed-banner">
                    <div>
                        <p className="font-medium">{t('surveys.translationFailedTitle', 'Automatic translation failed.')}</p>
                        <p className="text-xs mt-0.5 opacity-80">
                            {t('surveys.translationFailedBody', 'Your original content is safe. The system will retry shortly.')}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="text-xs underline opacity-70 hover:opacity-100"
                        onClick={() => setDismissedTx(true)}
                        data-testid="translation-failed-dismiss"
                    >
                        {t('common.dismiss', 'Dismiss')}
                    </button>
                </div>
            )}

            <Tabs defaultValue="basics">
                <TabsList>
                    <TabsTrigger value="basics" data-testid="tab-basics">{t('surveys.tabBasics')}</TabsTrigger>
                    <TabsTrigger value="questions" data-testid="tab-questions" data-insight-id="surveys.workflow.tab-questions" data-insight-label="Survey questions design tab">{t('surveys.tabQuestions')}</TabsTrigger>
                    <TabsTrigger value="appearance" data-testid="tab-appearance">{t('surveys.tabAppearance', 'Appearance')}</TabsTrigger>
                    <TabsTrigger value="closing" data-testid="tab-closing" data-insight-id="surveys.workflow.tab-closing" data-insight-label="Closing page tab">{t('surveys.tabClosing', 'Closing Page')}</TabsTrigger>
                    <TabsTrigger value="alerts" data-testid="tab-alerts">{t('surveys.tabAlerts')}</TabsTrigger>
                </TabsList>

                <TabsContent value="basics" className="space-y-4 mt-4">
                    <Card>
                        <CardHeader><CardTitle className="text-base">{t('surveys.basics')}</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <Label className="mb-2 block">
                                    {t('surveys.nameLabel', 'Survey name')}
                                    <span className="text-xs text-muted-foreground ml-2 font-normal">
                                        ({authoringLang === 'fr' ? 'FR' : authoringLang === 'es' ? 'ES' : 'EN'})
                                    </span>
                                </Label>
                                <Input
                                    value={survey.name?.[authoringLang] || ''}
                                    onChange={(e) => updateName(authoringLang, e.target.value)}
                                    data-testid="editor-name-input"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t('surveys.autoTranslateHint', 'Other languages will be translated automatically after save.')}
                                </p>
                            </div>
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-700" data-testid="editor-org-location-block">
                                <OrgLocationPicker
                                    organizationId={survey.organization_id || ''}
                                    locationId={survey.location_id || ''}
                                    disabled={!canManage}
                                    onChange={({ organizationId: o, locationId: l }) => {
                                        // Changing org resets the location so a
                                        // cross-org location can never persist.
                                        if (o !== (survey.organization_id || '')) {
                                            update({ organization_id: o, location_id: l || null });
                                        } else {
                                            update({ location_id: l || null });
                                        }
                                    }}
                                    testIdPrefix="editor"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t('surveys.orgLocationEditHelp', 'The organization and location this survey belongs to. The public URL below stays the same even if you change these.')}
                                </p>
                            </div>
                            <div className="flex items-center gap-3 pt-2">
                                <Switch checked={!!survey.enabled} onCheckedChange={(v) => update({ enabled: v })} data-testid="editor-enabled-switch" />
                                <span className="text-sm">{survey.enabled ? t('surveys.enabled') : t('surveys.disabled')}</span>
                            </div>
                            {/* P_SURVEY_EDITOR_UX_ENHANCEMENTS_1.0 — platform-
                                level Overall Experience toggle. Defaults to ON.
                                When ON, respondents see a 5-star opening screen
                                before any custom question. The rating is stored
                                as a regular rating answer so existing analytics,
                                Review Shield, and sentiment paths keep working. */}
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-700 space-y-2" data-testid="editor-overall-experience-section">
                                <div className="flex items-center gap-3">
                                    <Switch
                                        checked={survey.overall_experience_enabled !== false}
                                        onCheckedChange={(v) => update({ overall_experience_enabled: !!v })}
                                        data-testid="editor-overall-experience-switch"
                                        disabled={!canManage}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                            {t('surveys.overallExperience.toggleLabel', 'Show initial 5-star Overall Experience question')}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {t(
                                                'surveys.overallExperience.toggleHelp',
                                                'When enabled, respondents see a standard "How was your overall experience?" screen before your custom questions. Turn off for employee surveys, internal audits, or demographic surveys where this question is not appropriate.',
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-700">
                                <EnabledLanguagesPicker
                                    value={survey.enabled_languages ?? null}
                                    onChange={(next) => update({ enabled_languages: next })}
                                    allowInherit
                                    inheritedFrom={['en','fr','es']}
                                    disabled={!canManage}
                                    testIdPrefix="survey-languages"
                                />
                            </div>
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-700 space-y-2" data-testid="editor-public-url-section">
                                <Label className="block">
                                    {t('surveys.publicUrlLabel', 'Public survey URL')}
                                </Label>
                                <div className="flex items-center gap-2">
                                    <code
                                        className="flex-1 min-w-0 truncate text-xs sm:text-sm font-mono rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-2"
                                        data-testid="editor-public-url-value"
                                        title={`${window.location.origin}/s/${survey.slug}`}
                                    >
                                        {`${window.location.origin}/s/${survey.slug}`}
                                    </code>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={copyPublicLink}
                                        disabled={!survey?.slug}
                                        data-testid="editor-copy-public-url"
                                        className="flex-shrink-0"
                                    >
                                        <Copy className="w-3.5 h-3.5 mr-1.5" />
                                        {t('common.copyUrl', 'Copy URL')}
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {t('surveys.urlManagedHelp', 'This link is generated and managed automatically from your organization, location and survey name. It stays stable so existing links and QR codes keep working — you never have to create or edit it.')}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="questions" className="space-y-4 mt-4">
                    {/* P_BRANCHING_TREE_AUTHORING_1.0 — visual flow map +
                        linter + one-click leak healer at the top of the
                        Questions tab. Live updates as the author edits. */}
                    <SurveyFlowMap
                        questions={survey.questions || []}
                        onHeal={(fixed, count) => {
                            update({ questions: fixed });
                            toast.success(t('surveys.flowMap.healedToast', { count, defaultValue: 'Fixed {{count}} sibling-branch leak(s)' }));
                        }}
                    />
                    {/* P_AUTHORING_PARITY_1.0 — Authoring Language picker.
                        Same "Author Once, Translate Everywhere" model used
                        everywhere else in YulOnTap (Review Shield, Comms Hub).
                        The admin types in ONE language; on save the backend
                        translation pipeline auto-fills the other two. */}
                    <Card data-testid="authoring-language-card">
                        <CardContent className="pt-4 pb-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                    <Label className="block">{t('surveys.authoring.label', 'Authoring language')}</Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {t('surveys.authoring.help', 'Type once in your language. We translate to the others automatically when you save.')}
                                    </p>
                                </div>
                                <Select
                                    value={authoringLang}
                                    onValueChange={(v) => update({ authoring_language: v })}
                                >
                                    <SelectTrigger className="w-full sm:w-48" data-testid="authoring-language-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="en">English</SelectItem>
                                        <SelectItem value="fr">Français</SelectItem>
                                        <SelectItem value="es">Español</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>
                    {(survey.questions || []).map((q, idx) => {
                        // P_BRANCHING_TREE_AUTHORING_1.0 — Reached-from
                        // badge. Tells the author at a glance which
                        // branch this question lives in.
                        const flow = analyzeSurveyFlow(survey.questions || []);
                        const owner = flow.branchOf.get(q.id);
                        const reached = flow.reachedFrom.get(q.id);
                        const reachedTags = reached ? [...reached] : [];
                        const isUnreached = reachedTags.length === 0;
                        // P_SURVEY_EDITOR_UX_ENHANCEMENT_1.1 — informational
                        // banner shown above the FIRST visible question
                        // when its wording or type duplicates the
                        // built-in Overall Experience screen.
                        const showOverallDuplicateBanner =
                            idx === 0 && detectOverallExperienceDuplicate(survey);
                        return (
                        <div key={q.id} className="space-y-2">
                            {showOverallDuplicateBanner && (
                                <div
                                    className="rounded-md border border-sky-200 dark:border-sky-800 bg-sky-50/70 dark:bg-sky-950/30 px-3 py-2 text-sm text-sky-900 dark:text-sky-100 flex items-start gap-2"
                                    data-testid="overall-duplicate-banner"
                                    role="status"
                                >
                                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <p>
                                        {t(
                                            'surveys.overallExperience.duplicateBanner',
                                            'This survey already begins with the built-in Overall Experience screen. You may not need another similar first question.',
                                        )}
                                    </p>
                                </div>
                            )}
                        <Card data-testid={`question-card-${idx}`} data-question-id={q.id} className="transition-shadow duration-300">
                            <CardContent className="pt-4 space-y-3">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge variant="outline" className="font-mono text-xs">Q{idx + 1}</Badge>
                                        {/* Reached-from badge — branch owner OR "main flow" OR unreached warning */}
                                        {owner ? (
                                            <Badge
                                                className="bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-950/40 dark:text-fuchsia-200 text-xs"
                                                data-testid={`q-reached-from-${idx}`}
                                                title={t('surveys.reachedFromTip', 'This question is only shown when the respondent took this branch.')}
                                                data-insight-id={idx === 0 ? "surveys.workflow.reached-from" : undefined}
                                                data-insight-label={idx === 0 ? "Where this question is reached" : undefined}
                                            >
                                                {t('surveys.reachedFromBranch', 'Branch: ')}
                                                {owner.choiceLabel?.[authoringLang] || owner.choiceLabel?.en || owner.choiceId}
                                            </Badge>
                                        ) : isUnreached ? (
                                            <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200 text-xs" data-testid={`q-unreached-${idx}`}
                                                data-insight-id={idx === 0 ? "surveys.workflow.reached-from" : undefined}
                                                data-insight-label={idx === 0 ? "Where this question is reached" : undefined}
                                            >
                                                {t('surveys.unreachable', 'Unreachable')}
                                            </Badge>
                                        ) : reachedTags.length === 1 && reachedTags[0] === 'main' ? (
                                            <Badge variant="secondary" className="text-xs" data-testid={`q-main-flow-${idx}`}
                                                data-insight-id={idx === 0 ? "surveys.workflow.reached-from" : undefined}
                                                data-insight-label={idx === 0 ? "Where this question is reached" : undefined}
                                            >
                                                {t('surveys.mainFlow', 'Main flow')}
                                            </Badge>
                                        ) : (
                                            <Badge variant="secondary" className="text-xs" data-testid={`q-multi-path-${idx}`}
                                                data-insight-id={idx === 0 ? "surveys.workflow.reached-from" : undefined}
                                                data-insight-label={idx === 0 ? "Where this question is reached" : undefined}
                                            >
                                                {t('surveys.multiPath', 'Multi-path')}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch checked={!!q.enabled} onCheckedChange={(v) => updateQuestion(idx, { enabled: v })} data-testid={`q-enabled-${idx}`}
                                            data-insight-id={idx === 0 ? "surveys.workflow.toggle-question" : undefined}
                                            data-insight-label={idx === 0 ? "Pause or resume this question" : undefined}
                                        />
                                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeQuestion(idx)} data-testid={`q-delete-${idx}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                <Select value={q.response_type} onValueChange={(v) => updateQuestion(idx, { response_type: v, choices: v === 'multiple_choice_branching' ? (q.choices?.length ? q.choices : defaultBranchingChoices()) : q.choices })}>
                                    <SelectTrigger
                                        data-insight-id={idx === 0 ? "surveys.workflow.response-type" : undefined}
                                        data-insight-label={idx === 0 ? "Response type" : undefined}
                                    ><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="rating">{t('surveys.typeRating')}</SelectItem>
                                        <SelectItem value="yes_no_neutral">{t('surveys.typeYesNoNeutral')}</SelectItem>
                                        <SelectItem value="free_text">{t('surveys.typeFreeText')}</SelectItem>
                                        <SelectItem value="multiple_choice_branching">{t('surveys.typeMultipleChoiceBranching')}</SelectItem>
                                    </SelectContent>
                                </Select>
                                {/* P_AUTHORING_PARITY_1.0 — single-language authoring.
                                    The admin types the question in their chosen
                                    authoring language; the translation pipeline
                                    fills in EN/FR/ES automatically on save. */}
                                <div>
                                    <Input
                                        value={q.text?.[authoringLang] || ''}
                                        onChange={(e) => updateQuestionText(idx, authoringLang, e.target.value)}
                                        placeholder={t(`surveys.questionLabel.${authoringLang}`)}
                                        data-testid={`q-text-${idx}`}
                                        data-insight-id={idx === 0 ? "surveys.workflow.question-text" : undefined}
                                        data-insight-label={idx === 0 ? "Question wording" : undefined}
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t('surveys.autoTranslateHint', 'Other languages will be translated automatically after save.')}
                                    </p>
                                </div>
                                {/* P_SURVEY_ANSWER_ALERT_1.0 — per-answer SMS
                                    alerts. Off by default; the per-answer ticks
                                    are kept when it is switched off. */}
                                {(q.response_type === 'multiple_choice_branching' || q.response_type === 'yes_no_neutral') && (
                                    <div className="rounded-lg border border-dashed px-3 py-2 space-y-2" data-testid={`q-alert-section-${idx}`}>
                                        <div className="flex items-center justify-between gap-3">
                                            <Label className="text-xs leading-snug flex items-center gap-1.5">
                                                <Bell className="w-3.5 h-3.5 text-amber-500" />
                                                {t('surveys.answerAlert.questionSwitch', 'Does this question send an alert?')}
                                            </Label>
                                            <Switch
                                                checked={!!q.alert_on_answer}
                                                disabled={!hasAlertPhones}
                                                onCheckedChange={(v) => updateQuestion(idx, { alert_on_answer: !!v })}
                                                data-testid={`q-alert-switch-${idx}`}
                                            />
                                        </div>
                                        {!hasAlertPhones && (
                                            <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid={`q-alert-no-phones-${idx}`}>
                                                <a href="#survey-alert-phones" className="underline">
                                                    {t('surveys.answerAlert.noPhones', 'Add alert phone numbers first')}
                                                </a>
                                            </p>
                                        )}
                                        {hasAlertPhones && q.alert_on_answer && q.response_type === 'yes_no_neutral' && (
                                            <div className="space-y-1.5 pt-1" data-testid={`q-alert-options-${idx}`}>
                                                {(q.options || []).map((opt) => (
                                                    <label key={opt} className="flex items-center gap-2 text-xs cursor-pointer">
                                                        <Checkbox
                                                            checked={(q.alert_option_values || []).includes(opt)}
                                                            onCheckedChange={(v) => updateQuestion(idx, {
                                                                alert_option_values: v
                                                                    ? [...(q.alert_option_values || []), opt]
                                                                    : (q.alert_option_values || []).filter((x) => x !== opt),
                                                            })}
                                                            data-testid={`q-alert-option-${idx}-${opt}`}
                                                        />
                                                        <Bell className="w-3 h-3 text-amber-500" />
                                                        <span>{t('surveys.answerAlert.choiceLabel', 'Send an alert when this is chosen')} — {opt}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {q.response_type === 'multiple_choice_branching' && (
                                    <BranchingChoicesEditor
                                        question={q}
                                        questionIdx={idx}
                                        authoringLang={authoringLang}
                                        allQuestions={survey.questions || []}
                                        alertEnabled={!!q.alert_on_answer && hasAlertPhones}
                                        onChange={(choices) => updateQuestion(idx, { choices })}
                                        onCreateBranch={(choiceIdx) => createBranchFromChoice(idx, choiceIdx)}
                                    />
                                )}
                                {/* P_BRANCHING_TREE_ENGINE_1.2 — per-question
                                    flow control. Hidden for branching
                                    questions because branching uses
                                    per-choice next_step instead. */}
                                {q.response_type !== 'multiple_choice_branching' && (
                                    <QuestionFlowControl
                                        question={q}
                                        questionIdx={idx}
                                        allQuestions={survey.questions || []}
                                        onChange={(ns) => updateQuestion(idx, { next_step: ns })}
                                    />
                                )}
                            </CardContent>
                        </Card>
                        </div>
                        );
                    })}
                    <Button variant="outline" onClick={addQuestion} data-testid="add-question-btn"
                        data-insight-id="surveys.workflow.add-question"
                        data-insight-label="Add a question to this survey"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('surveys.addQuestion')}
                    </Button>
                </TabsContent>

                <TabsContent value="appearance" className="space-y-4 mt-4">
                    <div className="grid gap-4 lg:grid-cols-[1fr_minmax(320px,420px)]">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">{t('surveys.appearance.title', 'Appearance')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <ImageUploadControl
                                    label={t('surveys.appearance.logoLabel', 'Survey logo')}
                                    helpText={t('surveys.appearance.logoHelp', 'Displayed centered above the survey title. Leave empty to inherit your organization\'s brand logo (configured in Voucher Settings). PNG / JPG / SVG / WEBP up to 5MB.')}
                                    value={survey.logo_url || null}
                                    onChange={(url) => update({ logo_url: url })}
                                    previewHeight={80}
                                    previewMode="contain"
                                    disabled={!canManage}
                                    testIdPrefix="appearance-logo"
                                />

                                {/* v3.5.11 — Logo size picker (S/M/L/XL), matching the Review Shield pattern. */}
                                {survey.logo_url && (
                                    <div>
                                        <Label className="mb-2 block">
                                            {t('surveys.appearance.logoSizeLabel', 'Logo size')}
                                        </Label>
                                        <div className="flex gap-2">
                                            {[
                                                { value: 'small',      label: t('cardSettings.small', 'Small') },
                                                { value: 'medium',     label: t('cardSettings.medium', 'Medium') },
                                                { value: 'large',      label: t('cardSettings.large', 'Large') },
                                                { value: 'very_large', label: t('cardSettings.veryLarge', 'Extra Large') },
                                            ].map((sz) => (
                                                <Button
                                                    key={sz.value}
                                                    type="button"
                                                    variant={(survey.logo_size || 'medium') === sz.value ? 'default' : 'outline'}
                                                    size="sm"
                                                    onClick={() => update({ logo_size: sz.value })}
                                                    disabled={!canManage}
                                                    className="flex-1"
                                                    data-testid={`appearance-logo-size-${sz.value}`}
                                                >
                                                    {sz.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <Label className="mb-2 block">{t('surveys.appearance.displayModeLabel', 'Display mode')}</Label>
                                    <Select
                                        value={survey.display_mode || 'auto'}
                                        onValueChange={(v) => update({ display_mode: v })}
                                        disabled={!canManage}
                                    >
                                        <SelectTrigger className="w-full sm:w-64" data-testid="appearance-display-mode-trigger">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="auto" data-testid="appearance-display-mode-auto">
                                                {t('surveys.appearance.modeAuto', 'Auto (follow device)')}
                                            </SelectItem>
                                            <SelectItem value="light" data-testid="appearance-display-mode-light">
                                                {t('surveys.appearance.modeLight', 'Light')}
                                            </SelectItem>
                                            <SelectItem value="dark" data-testid="appearance-display-mode-dark">
                                                {t('surveys.appearance.modeDark', 'Dark')}
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t('surveys.appearance.displayModeHelp', 'Forces the public survey theme regardless of the respondent\'s device or browser settings.')}
                                    </p>
                                </div>

                                <ImageUploadControl
                                    label={t('surveys.appearance.backgroundLabel', 'Background image (wallpaper)')}
                                    helpText={t('surveys.appearance.backgroundHelp', 'Displayed as a full-page background. A subtle dark overlay is added automatically for readability.')}
                                    value={survey.background_image_url || null}
                                    onChange={(url) => update({ background_image_url: url })}
                                    previewHeight={80}
                                    previewMode="cover"
                                    accept="image/png,image/jpeg,image/webp,image/gif"
                                    disabled={!canManage}
                                    testIdPrefix="appearance-background"
                                />

                                {/* v3.5.19 — Card surface style (Frosted / Clear / Solid).
                                    Mirrors the Card Settings picker so survey admins can
                                    decide whether the welcome card blurs the wallpaper or
                                    lets it show through crisply. */}
                                <div data-testid="card-style-block">
                                    <Label className="mb-2 block">{t('cardStyle.label', 'Welcome card style')}</Label>
                                    <p className="text-xs text-muted-foreground mb-3">
                                        {t('surveys.appearance.cardStyleHelp', 'How the welcome card looks over your wallpaper. Applied across the language gate AND the survey screens.')}
                                    </p>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {[
                                            { value: '',        label: t('cardStyle.inheritOrg', 'Inherit'),   desc: t('cardStyle.inheritOrgDesc', 'Use organization default') },
                                            { value: 'frosted', label: t('cardStyle.frosted', 'Frosted'),       desc: t('cardStyle.frostedDesc', 'Translucent + blur') },
                                            { value: 'clear',   label: t('cardStyle.clear', 'Clear'),           desc: t('cardStyle.clearDesc', 'Wallpaper shows through') },
                                            { value: 'solid',   label: t('cardStyle.solid', 'Solid'),           desc: t('cardStyle.solidDesc', 'Fully opaque card') },
                                        ].map((opt) => {
                                            const current = survey.card_style || '';
                                            const isActive = current === opt.value;
                                            return (
                                                <button
                                                    key={opt.value || 'inherit'}
                                                    type="button"
                                                    onClick={() => update({ card_style: opt.value || null })}
                                                    disabled={!canManage}
                                                    className={`relative rounded-lg border-2 p-2 text-left transition-all ${
                                                        isActive
                                                            ? 'border-fuchsia-600 bg-fuchsia-50 dark:bg-fuchsia-950/30 dark:border-fuchsia-400'
                                                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                                                    } ${!canManage ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                    data-testid={`survey-card-style-option-${opt.value || 'inherit'}`}
                                                    aria-pressed={isActive}
                                                >
                                                    <div
                                                        className="h-10 rounded mb-2 flex items-center justify-center text-[10px] font-medium overflow-hidden relative"
                                                        style={{
                                                            background: opt.value === 'solid'
                                                                ? '#0f172a'
                                                                : 'linear-gradient(135deg,#475569 0%,#1e293b 100%)',
                                                        }}
                                                    >
                                                        <div
                                                            className={`absolute inset-1.5 rounded text-white text-[10px] flex items-center justify-center ${
                                                                opt.value === 'frosted' ? 'bg-white/10 backdrop-blur-sm border border-white/20' :
                                                                opt.value === 'clear'   ? 'bg-white/0 border border-white/30' :
                                                                opt.value === 'solid'   ? 'bg-slate-900 border border-slate-700' :
                                                                'bg-white/10 backdrop-blur-sm border border-white/20 border-dashed'
                                                            }`}
                                                        >
                                                            Aa
                                                        </div>
                                                    </div>
                                                    <div className="text-xs font-semibold text-slate-900 dark:text-slate-100">{opt.label}</div>
                                                    <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">{opt.desc}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* v3.5.1 — Live preview panel. Updates instantly as state changes; no save required. */}
                        {/* v3.5.2 — Device-frame toggle (Phone/Tablet/Desktop) constrains preview width locally. */}
                        <div className="lg:sticky lg:top-4 self-start">
                            <Card>
                                <CardHeader className="space-y-2 pb-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <CardTitle className="text-base">
                                            {t('surveys.appearance.previewTitle', 'Live preview')}
                                        </CardTitle>
                                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/50 px-2 py-0.5 rounded">
                                            {t('surveys.appearance.previewBadge', 'Unsaved changes shown')}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div
                                            role="group"
                                            aria-label={t('surveys.appearance.deviceLabel', 'Device view')}
                                            className="inline-flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden text-xs"
                                            data-testid="preview-device-toggle"
                                        >
                                            {[
                                                { id: 'phone',   label: t('surveys.appearance.devicePhone', 'Phone') },
                                                { id: 'tablet',  label: t('surveys.appearance.deviceTablet', 'Tablet') },
                                                { id: 'desktop', label: t('surveys.appearance.deviceDesktop', 'Desktop') },
                                            ].map(opt => {
                                                const active = previewDevice === opt.id;
                                                return (
                                                    <button
                                                        key={opt.id}
                                                        type="button"
                                                        onClick={() => setPreviewDevice(opt.id)}
                                                        aria-pressed={active}
                                                        className={`px-3 py-1.5 transition-colors ${
                                                            active
                                                                ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                                                                : 'bg-transparent text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-800'
                                                        }`}
                                                        data-testid={`preview-device-${opt.id}`}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap justify-end">
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={openPublicLink}
                                            disabled={!survey?.slug}
                                            aria-label={t('surveys.appearance.openPublicAria', 'Open public survey in new window')}
                                            data-testid="preview-open-public-link"
                                            className="text-xs h-8"
                                        >
                                            <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                                            {t('surveys.appearance.openPublic', 'Open public link')}
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={copyPublicLink}
                                            disabled={!survey?.slug}
                                            aria-label={t('surveys.appearance.copyPublicAria', 'Copy public survey link')}
                                            data-testid="preview-copy-public-link"
                                            className="text-xs h-8"
                                        >
                                            <Copy className="w-3.5 h-3.5 mr-1.5" />
                                            {t('surveys.appearance.copyPublic', 'Copy public link')}
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={downloadQrCode}
                                            disabled={!survey?.slug}
                                            aria-label={t('surveys.appearance.downloadQrAria', 'Download survey QR code')}
                                            data-testid="preview-download-qr"
                                            className="text-xs h-8"
                                        >
                                            <QrCode className="w-3.5 h-3.5 mr-1.5" />
                                            {t('surveys.appearance.downloadQr', 'Download QR code')}
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={downloadBrandedQr}
                                            disabled={!survey?.slug}
                                            aria-label={t('surveys.appearance.downloadBrandedQrAria', 'Download branded survey QR code')}
                                            data-testid="preview-download-branded-qr"
                                            className="text-xs h-8"
                                        >
                                            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                                            {t('surveys.appearance.downloadBrandedQr', 'Download branded QR')}
                                        </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div
                                        className="mx-auto transition-[max-width] duration-200 ease-out"
                                        style={{
                                            maxWidth: PREVIEW_WIDTHS[previewDevice]
                                                ? `${PREVIEW_WIDTHS[previewDevice]}px`
                                                : '100%',
                                        }}
                                        data-testid="preview-device-frame"
                                        data-device={previewDevice}
                                    >
                                        <SurveyAppearancePreview
                                            logoUrl={survey.logo_url || survey.effective_logo_url || null}
                                            logoSize={survey.logo_size || 'medium'}
                                            backgroundImageUrl={survey.background_image_url || null}
                                            displayMode={survey.display_mode || 'auto'}
                                            cardStyle={survey.card_style || orgCardStyle}
                                            title={survey.name?.[i18n.language] || survey.name?.en || t('surveys.previewSampleTitle', 'Survey title')}
                                            description={survey.description?.[i18n.language] || survey.description?.en || ''}
                                            data-testid="appearance-live-preview"
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        {t('surveys.appearance.previewHelpCardStyle', 'Reflects logo, background, display mode and card style in real time. Theme is scoped to this preview only.')}
                                    </p>

                                    {/* v3.5.5 — Offscreen high-res canvas for QR download. */}
                                    {survey?.slug && (
                                        <div
                                            ref={qrCanvasRef}
                                            aria-hidden="true"
                                            style={{ position: 'absolute', left: '-10000px', top: '-10000px', width: 1, height: 1, overflow: 'hidden', pointerEvents: 'none' }}
                                            data-testid="preview-qr-offscreen"
                                        >
                                            <QRCodeCanvas
                                                value={`${window.location.origin}/s/${survey.slug}`}
                                                size={1024}
                                                level="H"
                                                includeMargin={true}
                                                bgColor="#ffffff"
                                                fgColor="#000000"
                                            />
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </TabsContent>

                {/* P_SURVEY_CLOSING_PAGE_1.1 — Closing page editor.
                    Author Once, Translate Everywhere: inputs bind to
                    the chosen authoring language; backend translates
                    the others on save. Closing page is opt-in. */}
                <TabsContent value="closing" className="space-y-4 mt-4" data-testid="closing-page-tab">
                    <ClosingPageEditor
                        closingPage={survey.closing_page || {}}
                        authoringLang={authoringLang}
                        organizationId={survey.organization_id}
                        onChange={(cp) => update({ closing_page: cp })}
                    />
                </TabsContent>

                <TabsContent value="alerts" className="space-y-4 mt-4">
                    <Card>
                        <CardHeader><CardTitle className="text-base">{t('surveys.alertSettings')}</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center gap-3">
                                <Switch
                                    checked={!!survey.alert_rules?.trigger_on_low_rating}
                                    onCheckedChange={(v) => updateAlerts({ trigger_on_low_rating: v })}
                                    data-testid="alert-toggle-low-rating"
                                    data-insight-id="surveys.workflow.low-rating-alert"
                                    data-insight-label="Active listening for distress signals"
                                />
                                <span className="text-sm">{t('surveys.alertOnLowRating')}</span>
                            </div>
                            <div>
                                <Label className="mb-2 block">{t('surveys.threshold')}</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={5}
                                    value={survey.alert_rules?.low_rating_threshold ?? 3}
                                    onChange={(e) => updateAlerts({ low_rating_threshold: parseInt(e.target.value, 10) || 3 })}
                                    className="w-24"
                                    data-testid="alert-threshold-input"
                                />
                                <p className="text-xs text-muted-foreground mt-1">{t('surveys.thresholdHelp')}</p>
                            </div>
                            <div>
                                <Label className="mb-2 flex items-center gap-2"><Mail className="w-4 h-4" />{t('surveys.alertEmails')}</Label>
                                <Input
                                    placeholder="alerts@yourbusiness.com, manager@yourbusiness.com"
                                    value={(survey.notification_emails || []).join(', ')}
                                    onChange={(e) => update({
                                        notification_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                                    })}
                                    data-testid="alert-emails-input"
                                    data-insight-id="surveys.workflow.alert-recipients"
                                    data-insight-label="Who receives the distress signal"
                                />
                            </div>
                            <div>
                                <Label className="mb-2 flex items-center gap-2" id="survey-alert-phones"><Phone className="w-4 h-4" />{t('surveys.alertPhones')}</Label>
                                <Input
                                    placeholder="+15145551234, +15145555678"
                                    value={(survey.notification_phones || []).map(p => p.phone_number || p.phone || p).join(', ')}
                                    onChange={(e) => update({
                                        notification_phones: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                                            .map(num => ({ phone_number: num }))
                                    })}
                                    data-testid="alert-phones-input"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* P_SURVEYS_GUIDANCE_PARITY_1.0 — Quick Tour modal. Auto-opens
                on first visit (per browser), re-openable from the header
                "Quick tour" button. Content mirrors the operational seeds
                so tooltips, the tour, and the manuals stay consistent. */}
            <SurveyBuilderQuickTour
                open={tourOpen}
                onClose={() => setTourOpen(false)}
            />

            {/* P_SURVEY_EDITOR_UX_ENHANCEMENTS_1.0 — unsaved-changes guard
                when the author clicks "Back to Surveys" mid-edit. */}
            <AlertDialog open={unsavedConfirmOpen} onOpenChange={setUnsavedConfirmOpen}>
                <AlertDialogContent data-testid="survey-unsaved-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t('surveys.editor.unsavedTitle', 'You have unsaved changes')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('surveys.editor.unsavedBody', 'If you leave now, your edits to this survey will be lost. Would you like to save them first?')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="survey-unsaved-cancel">
                            {t('common.cancel', 'Cancel')}
                        </AlertDialogCancel>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => { setUnsavedConfirmOpen(false); navigate('/surveys'); }}
                            data-testid="survey-unsaved-discard"
                        >
                            {t('surveys.editor.unsavedDiscard', 'Discard Changes')}
                        </Button>
                        <AlertDialogAction
                            data-testid="survey-unsaved-save"
                            onClick={async () => {
                                setUnsavedConfirmOpen(false);
                                await handleSave();
                                navigate('/surveys');
                            }}
                        >
                            {t('common.save', 'Save')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default SurveyEditor;
